import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

export type ServiceStatus = 'online' | 'offline' | 'unknown'

interface ServiceHealthState {
  gladia: ServiceStatus
  anthropic: ServiceStatus
  telnyx: ServiceStatus
  translationVps: ServiceStatus
  resend: ServiceStatus
  wave: ServiceStatus
}

interface ServiceHealthContextType {
  health: ServiceHealthState
  isAnyDown: boolean
  lastChecked: Date | null
}

const ServiceHealthContext = createContext<ServiceHealthContextType>({
  health: {
    gladia: 'unknown',
    anthropic: 'unknown',
    telnyx: 'unknown',
    translationVps: 'unknown',
    resend: 'unknown',
    wave: 'unknown',
  },
  isAnyDown: false,
  lastChecked: null,
})

export function ServiceHealthProvider({ children }: { children: React.ReactNode }) {
  const [health, setHealth] = useState<ServiceHealthState>({
    gladia: 'unknown',
    anthropic: 'unknown',
    telnyx: 'unknown',
    translationVps: 'unknown',
    resend: 'unknown',
    wave: 'unknown',
  })
  const [lastChecked, setLastChecked] = useState<Date | null>(null)

  const checkHealth = useCallback(async () => {
    // Check translation VPS reachability (no-cors — we only need network status, not response body)
    let translationVps: ServiceStatus = 'unknown'
    try {
      const res = await fetch('https://translate.getcallro.com/health', {
        method: 'GET',
        mode: 'no-cors',                     // suppresses CORS preflight errors in console
        signal: AbortSignal.timeout(5000),
      })
      // no-cors response.type === 'opaque' — if fetch() resolved, server is reachable
      translationVps = res.type === 'opaque' || res.ok ? 'online' : 'offline'
    } catch {
      translationVps = 'offline'
    }

    // Check all other services via Edge Function health endpoint
    // Failures are silently caught — CORS block on this endpoint is expected until
    // the health-check function has its CORS headers properly configured.
    let serviceStatuses: Partial<ServiceHealthState> = {}
    try {
      const { data } = await supabase.functions.invoke('health-check', {
        body: {},
      })
      if (data) {
        serviceStatuses = data as Partial<ServiceHealthState>
      }
    } catch {
      // Edge Function unreachable or CORS blocked — leave statuses as 'unknown'
    }

    setHealth(prev => ({
      ...prev,
      translationVps,
      gladia: serviceStatuses.gladia ?? prev.gladia,
      anthropic: serviceStatuses.anthropic ?? prev.anthropic,
      telnyx: serviceStatuses.telnyx ?? prev.telnyx,
      resend: serviceStatuses.resend ?? prev.resend,
      wave: serviceStatuses.wave ?? prev.wave,
    }))
    setLastChecked(new Date())
  }, [])

  useEffect(() => {
    void checkHealth()
    const interval = setInterval(() => { void checkHealth() }, 60_000)
    return () => clearInterval(interval)
  }, [checkHealth])

  const isAnyDown = Object.values(health).some(s => s === 'offline')

  return (
    <ServiceHealthContext.Provider value={{ health, isAnyDown, lastChecked }}>
      {children}
    </ServiceHealthContext.Provider>
  )
}

export function useServiceHealth() {
  return useContext(ServiceHealthContext)
}

/**
 * Degradation matrix — what to show when each service is offline.
 */
export const DEGRADATION_MATRIX: Record<keyof ServiceHealthState, string> = {
  gladia: 'Live transcription offline. Calls continue normally. Post-call transcript still runs via VPS.',
  anthropic: 'AI Coach offline. Chat in "Leave a message" mode. Summaries will retry when service restores.',
  telnyx: 'Phone system offline. Use chat and SMS to reach leads.',
  translationVps: 'Spanish translation offline. Emergency unmute active.',
  resend: 'Email reports queued. Will send when service restores.',
  wave: 'Accounting sync paused. Transactions queued in pending_wave_sync.',
}
